import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Upload,
  Empty,
  Pagination,
  Table,
  Modal,
  Image,
  Spin,
} from "antd";
import { UploadOutlined, FileAddOutlined } from "@ant-design/icons";
import { BsFillPersonFill } from "react-icons/bs";
import usePersonnelDocumentService from "../../../services/usePersonnelDocumentService";
import { useLocation, useNavigate } from "react-router-dom";
import PageRoutes from "../../../utils/routes";
import FirmDocumentColumns from "./Components/FirmDocumentColumns";
import useFirmFileService from "../../../services/useFirmFileService";

const DocumentUploadFirm = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { personnelName, personnelSurname, personnelId } = state || {};

  const [fileList, setFileList] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [pagination, setPagination] = useState({ page: 0, size: 10 });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handlePreview = (fileUrl) => {
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl);

    if (isImage) {
      setPreviewUrl(fileUrl);
      setIsModalOpen(true);
    } else {
      window.open(fileUrl, "_blank");
    }
  };

  const { useAddFileFirm, useDeleteFileFirm, useGetAllFilesByPersonnelIdFirm } =
    useFirmFileService();

  const { data: filesResponse, isLoading: isFirmFilesLoading } =
    useGetAllFilesByPersonnelIdFirm({ ...pagination, personnelId });

  const files = filesResponse?.data?.data || [];
  const totalItems = filesResponse?.data?.total || 0;

  const { mutateAsync: deleteFirmFile, isPending: isDeleteFirmFileLoading } =
    useDeleteFileFirm();

  const { mutateAsync: addFirmFile, isPending: isAddFirmFileLoading } =
    useAddFileFirm();

  useEffect(() => {
    if (!personnelName || !personnelSurname || personnelId === undefined) {
      navigate(PageRoutes.documentManagement);
    }
  }, [personnelName, personnelSurname, personnelId, navigate]);

  const handleUpload = async () => {
    if (!fileList[0]) return;

    const requestBody = {
      personnelId: personnelId,
      title: title,
      isBackoffice: false,
      description: description,
    };

    let formData = new FormData();
    const json = JSON.stringify(requestBody);
    const blob = new Blob([json], {
      type: "application/json",
    });

    const file = fileList[0].originFileObj || fileList[0];

    formData.append("request", blob);
    formData.append("file", file);

    try {
      await addFirmFile(formData);
      setFileList([]);
      setTitle("");
      setDescription("");
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination({ page: page - 1, size: pageSize });
  };

  const deleteFile = (id) => {
    deleteFirmFile(id);
  };

  const props = {
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    fileList,
    onRemove: () => {
      setFileList([]);
    },
  };

  const isLoading =
    isFirmFilesLoading || isAddFirmFileLoading || isDeleteFirmFileLoading;

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.personInfo}>
        <div style={styles.avatar}>
          <BsFillPersonFill size={32} color="#595959" />
        </div>
        <div style={styles.personDetails}>
          <div style={styles.personName}>
            {personnelName} {personnelSurname}
          </div>
          <div style={styles.personId}>Personel ID: {personnelId}</div>
        </div>
      </div>

      <div style={styles.uploadSection}>
        <div style={styles.uploadLabel}>Dosya Başlığı</div>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Dosya başlığı giriniz"
          style={{ marginBottom: "16px" }}
        />

        <div style={styles.uploadLabel}>Dosya Açıklaması</div>
        <Input.TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Dosya açıklaması giriniz"
          style={{ marginBottom: "16px" }}
          rows={4}
        />

        <div style={styles.uploadLabel}>Dosya Seçiniz</div>
        <Upload {...props} maxCount={1}>
          <Button icon={<UploadOutlined />}>Choose File</Button>
        </Upload>

        <Button
          type="primary"
          onClick={handleUpload}
          disabled={fileList.length === 0 || !title || !description}
          style={{ marginTop: "16px" }}
          icon={<FileAddOutlined />}>
          Yükle
        </Button>
      </div>

      <div style={styles.fileList}>
        {files.length === 0 ? (
          <Empty
            description={
              <div>
                <div>Henüz eklenmiş hiç dosyanız yok</div>
                <div style={{ marginTop: "8px" }}>
                  Yukarıda yer alan formu kullanarak hemen yeni bir dosya
                  ekleyebilirsiniz
                </div>
              </div>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <>
            <Table
              columns={FirmDocumentColumns(deleteFile, handlePreview)}
              dataSource={files}
              pagination={false}
              rowKey="id"
              loading={isLoading}
            />
            <Modal
              open={isModalOpen}
              footer={null}
              onCancel={() => setIsModalOpen(false)}
              width={800}>
              {previewUrl && (
                <Image src={previewUrl} style={{ width: "100%" }} />
              )}
            </Modal>
            <div style={styles.pagination}>
              <Pagination
                current={pagination.page + 1}
                total={totalItems}
                pageSize={pagination.size}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    background: "#fff",
    width: "90%",
    margin: "0 auto",
    padding: "24px",
  },
  uploadSection: {
    backgroundColor: "#fff",
    padding: "24px",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    marginBottom: "24px",
  },
  uploadLabel: {
    fontSize: "16px",
    fontWeight: "500",
    marginBottom: "12px",
    color: "#262626",
  },
  fileList: {
    marginTop: "24px",
    padding: "24px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
  },
  emptyState: {
    padding: "48px",
    textAlign: "center",
  },
  personInfo: {
    display: "flex",
    alignItems: "center",
    marginBottom: "24px",
    padding: "16px",
    backgroundColor: "#fafafa",
    borderRadius: "8px",
    border: "1px solid #f0f0f0",
  },
  avatar: {
    width: "48px",
    height: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f0f0",
    borderRadius: "50%",
    marginRight: "16px",
  },
  personDetails: {
    display: "flex",
    flexDirection: "column",
  },
  personName: {
    fontSize: "18px",
    fontWeight: "500",
    color: "#262626",
  },
  personId: {
    fontSize: "14px",
    color: "#8c8c8c",
    marginTop: "4px",
  },
  pagination: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "flex-end",
  },
  loading: {
    textAlign: "center",
    padding: "24px",
  },
};

export default DocumentUploadFirm;
